import React from 'react';
import classnames from 'classnames';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import styles from './aiSearchModal.module.scss';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import {
  AISearchTypes,
  AISearchType,
  AISearchRequiresFiles,
} from 'v2/utils/AISearchUtils';
import MultiSelectDataVendors from 'v2/form/CustomSelectors/Multiselect/MultiSelectDataVendors';
import { AIConversationSearchType, ItemList, RootState } from 'v2/models';
import { hasPermission, PERMISSION_NAME } from 'v2/utils/permissions';
import { useSelector } from 'react-redux';
import AISearchFiles from './AISearchFiles';
import { AISearchFormFields } from './AISearchSchema';
import AICustomChatFormField from './AICustomChatFormField';

const AISearchForm = () => {
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );
  const { control, setValue, watch } = useFormContext<AISearchFormFields>();
  const companyList = useWatch({ control, name: 'companyList' });
  const fileTypes = useWatch({ control, name: 'fileTypes' });
  const searchType = useWatch({ control, name: 'searchType' });

  const isCompaniesDisabled = searchType === '';

  const isFilesDisabled =
    companyList.is_all ||
    companyList.list.length > 1 ||
    (!companyList.is_all && companyList.list.length === 0);

  const validateInput = (value: ItemList) => {
    if (!value.is_all) {
      return value.list.length > 0;
    }
    return true;
  };

  const options: Array<{
    label: AISearchType;
    value: AIConversationSearchType;
  }> = [];

  if (hasPermission(permissions, [PERMISSION_NAME.DOCUMENT_CHAT]))
    options.push({
      label: AISearchTypes[AIConversationSearchType.DOCUMENT],
      value: AIConversationSearchType.DOCUMENT,
    });
  if (hasPermission(permissions, [PERMISSION_NAME.DRM_CHAT]))
    options.push({
      label: AISearchTypes[AIConversationSearchType.DRM],
      value: AIConversationSearchType.DRM,
    });

  return (
    <div className={classnames(styles.descriptionForm)}>
      <FormLabel label={`What type of search is this?`} required={false} />
      <Controller
        name="searchType"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field: { onChange } }) => {
          return (
            <GeneralSelect
              options={options}
              handleChange={(option) => onChange(option?.value)}
              placeHolder="Select search type"
              customStyles={{
                control: {
                  width: '100%',
                  borderColor: 'var(--light-gray)',
                  cursor: 'pointer',
                },
                dropdownIndicator: {
                  right: '10px',
                  justifyContent: 'flex-end',
                },
              }}
            />
          );
        }}
      />
      <AICustomChatFormField />
      <FormLabel
        label={`Which companies would you like to search?`}
        required={false}
        className={classnames('mt-4', styles.label, {
          [styles.disabled]: isCompaniesDisabled,
        })}
      />
      <Controller
        name="companyList"
        control={control}
        rules={{
          required: true,
          validate: validateInput,
        }}
        render={({ field: { onChange } }) => {
          return (
            <MultiSelectDataVendors
              onChange={(value) => {
                if (!value) return;
                const newValue = {
                  is_all: value?.is_all || value?.list.length > 1,
                  list: [],
                };
                setValue('fileList', newValue);
                onChange(value);
              }}
              defaultValues={control._defaultValues.companyList as ItemList}
              disabled={isCompaniesDisabled}
              containFiles={AISearchRequiresFiles[searchType]}
            />
          );
        }}
      />

      <AISearchFiles
        fileTypes={fileTypes}
        companyList={companyList}
        isFilesDisabled={isFilesDisabled}
        searchType={searchType}
        control={control}
        validateInput={validateInput}
        defaultValues={watch('fileList')}
      />
    </div>
  );
};

export default AISearchForm;
